// DS: C86
/** An animation put in place while we load the actual content */
import React from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import Loader from '../Loader';
import OutlinedDASymbol from '../OutlinedDASymbol';
import { usePortalNode } from '../popper/hooks/usePortalNode';
import { useLoaderDelay } from './useLoaderDelay';

import s from './LoadingIndicator.scss';

export interface Props {
  className?: string;
  spinnerClassName?: string;
  view?: 'llama' | 'spinner' | 'daSymbol' | 'daSymbolPurple' | 'watchSuggestions';
  coverMode?: boolean; // Fill the block we are in
  withOverlay?: boolean; // Add overlay
  usePortal?: boolean; // Portalized fullpage takeover
  forSSR?: boolean; // Force render when you want to show it on SSR (default: false)
  fillViewport?: boolean; // Fill the entire viewport height and center the loader
}

const LoadingIndicatorPortal = ({ children }) => {
  const portalContainer = usePortalNode('loadingIndicator');
  return portalContainer && ReactDOM.createPortal(children, portalContainer);
};

const LoadingIndicator: React.FC<Props> = ({
  view = 'spinner',
  coverMode = false,
  withOverlay = false,
  className,
  spinnerClassName,
  usePortal = false,
  forSSR = false,
  fillViewport = false,
}) => {
  // Delayed showing for better UX
  const showLoader = useLoaderDelay();

  const content = (
    <div
      className={classnames(
        s['root'],
        (showLoader || forSSR) && s['visible'],
        s[`view-${view}`],
        coverMode && s['cover-mode'],
        withOverlay && s['with-overlay'],
        usePortal && s['fixed-position'],
        fillViewport && s['fill-viewport'],
        className
      )}
    >
      {
        {
          daSymbol: <OutlinedDASymbol />,
          daSymbolPurple: <OutlinedDASymbol palette="purple" />,
          watchSuggestions: <OutlinedDASymbol palette="green-to-purple" />,
          llama: <Loader size="big" />,
          spinner: <div className={classnames(s['spinner'], spinnerClassName)} />,
        }[view]
      }
    </div>
  );

  if (usePortal) {
    return <LoadingIndicatorPortal>{content}</LoadingIndicatorPortal>;
  }

  return content;
};
LoadingIndicator.displayName = 'LoadingIndicator';

export default LoadingIndicator;
